/**
 * <shop-menu> — desktop "Shop" nav item: real link (click navigates to /collections/all)
 * that also discloses a small dropdown on hover / keyboard focus. Desktop (>=990px) only;
 * inert on mobile/tablet where the drawer nav takes over.
 */
class ShopMenu extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('a');
    this.menu = this.querySelector('.shop-menu__list');
    this.closeDelay = 150;
    this.closeTimer = null;

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentCloseSignal = this.onDocumentCloseSignal.bind(this);
  }

  connectedCallback() {
    if (!this.trigger || !this.menu) return;

    this.overlay = document.querySelector('[data-shop-menu-overlay]');

    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeyDown);

    // Search or cart opening closes Shop; a click anywhere else outside closes it too.
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('senve:close-shop-menu', this.onDocumentCloseSignal);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('senve:close-shop-menu', this.onDocumentCloseSignal);
    this.close();
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    // Cancel any pending close BEFORE the early return: without this, moving the pointer
    // from the trigger through the gap into the dropdown re-enters while still .is-open
    // (mouseleave/mouseenter fire on gap transit, but the close timer from the leave hasn't
    // fired yet), and the early return would skip clearing it — closing the menu out from
    // under the user a moment later.
    window.clearTimeout(this.closeTimer);
    if (this.isOpen()) return;

    // Let any other open shop-menu (or the search/cart overlays) close first.
    document.dispatchEvent(new CustomEvent('senve:close-shop-menu', { detail: { except: this } }));

    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');

    const header = document.querySelector('.header-wrapper');
    if (header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(header.getBoundingClientRect().bottom)}px`
      );
    }

    if (this.overlay) this.overlay.classList.add('is-active');
  }

  close() {
    window.clearTimeout(this.closeTimer);
    if (!this.isOpen()) return;

    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    if (this.overlay) this.overlay.classList.remove('is-active');
  }

  scheduleClose() {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.close(), this.closeDelay);
  }

  onMouseEnter() {
    this.open();
  }

  onMouseLeave() {
    this.scheduleClose();
  }

  onFocusIn() {
    if (this.suppressFocusOpen) return;
    this.open();
  }

  onFocusOut() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyDown(event) {
    if (event.code !== 'Escape') return;
    // Focusing the trigger below fires a focusin, which would normally reopen the menu
    // (see onFocusIn) — suppress that one reopen so Escape actually closes.
    this.suppressFocusOpen = true;
    this.close();
    this.trigger.focus();
    window.setTimeout(() => {
      this.suppressFocusOpen = false;
    });
  }

  onDocumentClick(event) {
    if (this.contains(event.target)) return;
    this.close();
  }

  onDocumentCloseSignal(event) {
    if (event.detail && event.detail.except === this) return;
    this.close();
  }
}

customElements.define('shop-menu', ShopMenu);

// Search and cart each open their own overlay — Shop must never stay open underneath them.
document.addEventListener(
  'click',
  (event) => {
    if (event.target.closest('.header__icon--summary, #cart-icon-bubble')) {
      document.dispatchEvent(new CustomEvent('senve:close-shop-menu'));
    }
  },
  true
);
